import { Component, OnInit, Type, ViewChild } from '@angular/core';
import { ColmecWorkingOrderStepComponent } from '../colmec-working-order-step/colmec-working-order-step.component';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import {
  OperatorFunction,
  Observable,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  forkJoin,
  of,
  map,
  first,
  catchError,
  filter,
  merge,
  Subject
} from 'rxjs';
import {
  AuthService,
  PriceService,
  ProductService,
  gungAddRemoveSpinner,
  Customer,
  SelectedCustomerService
} from 'gung-standard';
import { GungUser as User } from 'gung-standard';
import {
  ColmecWorkOrderTire,
  ColmecTirePrice
} from '../../colmec-working-order-wrapper/colmec-working-order-wrapper.component';
import { GungTextInputTypeaheadComponent, OptionsList } from 'gung-common';
import { ColmecTireWorkingOrderService, ColmecTire } from '../../../../services/colmec-tire-working-order.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'colmec-working-order-tires-step',
    templateUrl: './colmec-working-order-tires-step.component.html',
    styleUrls: ['./colmec-working-order-tires-step.component.scss'],
    standalone: false
})
export class ColmecWorkingOrderTiresStepComponent extends ColmecWorkingOrderStepComponent implements OnInit {
  @ViewChild('gungTextInputTypeahead', { static: true }) gungTextInputTypeahead: any;

  public form: FormGroup;
  public articles;
  protected currentUser: User;
  protected selectedCustomer: Customer;
  public calculatedTotals: { qty: number; total: number; currency: string };
  public tirePositionOptionsList: OptionsList[] = [];
  public tireAxleOptionsList: OptionsList[] = [];

  cachedTiresWorkingOrder: ColmecTire[];

  resultFormatterItemId = (item: any) => {
    if (!!item.name && !!item.manufacturer && !!item.id) {
      return `${item.name} - ${item.manufacturer} - ${item.id}`;
    } else if (!!item.name && !!item.id) {
      return `${item.name} - ${item.id}`;
    } else if (!!item.name) {
      return `${item.name}`;
    } else {
      return '';
    }
  };

  itemIdSearchTypeahead: OperatorFunction<string, readonly ColmecWorkOrderTire[]> = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    const clicksWithClosedPopup$: Subject<string> = this.gungTextInputTypeahead.click$.pipe(
      filter(() => !this.gungTextInputTypeahead.instance.isPopupOpen())
    );
    const inputFocus$: Subject<string> = this.gungTextInputTypeahead.focus$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      switchMap(term => {
        try {
          return of(
            this.cachedTiresWorkingOrder.filter(tire => {
              const terms: string[] = term.split(' ');
              let testTerms: string[] = [
                tire?.id?.toLowerCase() || '',
                ...tire?.name?.split(' ')?.map(e => e?.toLowerCase())
              ];

              if (tire?.measures) {
                testTerms = [...testTerms, ...tire?.measures?.split(' ')?.map(e => e?.toLowerCase())];
              }

              if (tire?.supplierName) {
                testTerms = [...testTerms, ...tire?.supplierName?.split(' ')?.map(e => e?.toLowerCase())];
              }

              if (tire?.treadPattern) {
                testTerms = [...testTerms, ...tire?.treadPattern?.split(' ')?.map(e => e?.toLowerCase())];
              }

              return terms.every(term => testTerms.join(' ').includes(term.toLowerCase()));
            })
          );
        } catch (error) {
          return of([]);
        }
      }),
      map(tires => {
        return tires.length < 1 ? [] : tires.slice(0, 10);
      })
    );
  };

  public noTiresSelected: boolean = false;

  constructor(
    protected authService: AuthService,
    protected selectedCustomerService: SelectedCustomerService,
    protected productService: ProductService,
    protected priceService: PriceService,
    protected colmecTireWorkingOrderService: ColmecTireWorkingOrderService,
    protected translateService: TranslateService
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.onNextBtnClicked.subscribe(_ => this.handleNextButtonClicked());
    this.onPreviousBtnClicked.subscribe(_ => this.handlePreviousButtonClicked());
    this.initForm();

    ['A', 'B', 'C', 'D', 'E'].map(row => {
      for (let i = 1; i < 5; i++) {
        this.tirePositionOptionsList.push({
          id: row + i,
          name: row + i
        });
      }
    });

    this.tireAxleOptionsList = [
      {
        id: 'Styr',
        name: this.translateService.instant('STEER_AXLE_OPTION')
      },
      {
        id: 'Driv',
        name: this.translateService.instant('DRIVE_AXLE_OPTION')
      },
      {
        id: 'Löp',
        name: this.translateService.instant('RUN_AXLE_OPTION')
      }
    ];

    this.authService
      .getCurrentUser()
      .pipe(
        first(),
        switchMap(currentUser =>
          forkJoin({
            currentUser: of(currentUser),
            selectedCustomer: this.selectedCustomerService.getSelectedCustomer().pipe(first())
          })
        )
      )
      .subscribe(({ currentUser, selectedCustomer }) => {
        this.currentUser = currentUser;
        this.selectedCustomer = selectedCustomer;
        this.calculateTotals();
      });

    this.colmecTireWorkingOrderService
      .getWorkingOrderTiresList(this.workingOrder.agreementId)
      .pipe(
        first(),
        catchError(_ => of([]))
      )
      .subscribe(tires => {
        if (tires.length === 0) {
          this.form.get('article').disable();
        } else {
          this.form.get('article').enable();
        }

        this.cachedTiresWorkingOrder = tires.filter(t => !t.id.includes('-k'));
      });
  }

  public getLabel(): string {
    return 'TIRES';
  }

  private handleNextButtonClicked(): void {
    /*if (this.workingOrder.tires.length === 0) {
      this.noTiresSelected = true;
      return;
    }*/

    this.stepDone.emit(this.workingOrder);
  }

  private handlePreviousButtonClicked(): void {
    this.stepPrevious.emit(this.workingOrder);
  }

  protected initForm(): void {
    this.form = new FormGroup({
      article: new FormControl('', [Validators.required]),
      position: new FormControl('', [Validators.required]),
      tireAxle: new FormControl('', [Validators.required])
    });
  }

  public selectArticle({ item, preventDefault, target }) {
    this.form.get('article').setValue(item);
  }

  protected addToWorkingOrder({ target }): void {
    gungAddRemoveSpinner(target);
    const rawForm: { article: ColmecTire; position: string; tireAxle: string } = this.form.getRawValue();

    if (rawForm.article.tireType === 'LV') {
      forkJoin({
        customerPrice: this.priceService.getCustomerPrice(rawForm.article.id, this.selectedCustomer.id),
        framePrice: !!rawForm.article.artnrtillvalart
          ? this.priceService.getCustomerPrice(rawForm.article.artnrtillvalart, this.selectedCustomer.id)
          : of(null)
      }).subscribe(({ customerPrice, framePrice }) => {
        let netPrice = customerPrice.customerNetPrice.value;

        if (rawForm.article.retreaded && !!framePrice && !!framePrice.customerNetPrice) {
          netPrice += framePrice.customerNetPrice.value;
        }

        const tirePrice: ColmecTirePrice = {
          id: null,
          name: null,
          extra: {},
          value: netPrice,
          currency: customerPrice.customerNetPrice.currencyCode,
          discountPercentage: customerPrice.customerDiscountPercent
        };

        if (!!tirePrice.discountPercentage) {
          tirePrice.valueNoDiscount = customerPrice.customerGrossPrice.value;
          if (rawForm.article.retreaded && !!framePrice && !!framePrice.customerNetPrice) {
            tirePrice.valueNoDiscount += framePrice.customerGrossPrice.value;
          }
        }

        const tire: ColmecWorkOrderTire = {
          id: rawForm.article.id,
          qty: 1,
          name: rawForm.article.name,
          extra: {},
          position: rawForm.position,
          tireAxle: rawForm.tireAxle,
          supplierArticleNumber: rawForm.article.supplierArticleNumber,
          price: tirePrice,
          errors: rawForm.article.errors
        };

        this.workingOrder.tires.push(tire);
        this.noTiresSelected = false;
        this.calculateTotals();
        gungAddRemoveSpinner(target);
        this.resetForm(this.form);
      });
    } else {
      const tirePrice: ColmecTirePrice = {
        id: null,
        name: null,
        extra: {},
        value: !!rawForm.article.price.discountPercentage
          ? rawForm.article.price.value * (1 - rawForm.article.price.discountPercentage / 100)
          : rawForm.article.price.value,
        currency: rawForm.article.price.currency,
        discountPercentage: rawForm.article.price.discountPercentage
      };

      if (!!tirePrice.discountPercentage) {
        tirePrice.valueNoDiscount = rawForm.article.price.value;
      }

      const tire: ColmecWorkOrderTire = {
        id: rawForm.article.id,
        qty: 1,
        name: rawForm.article.name,
        extra: {},
        position: rawForm.position,
        tireAxle: rawForm.tireAxle,
        supplierArticleNumber: rawForm.article.supplierArticleNumber,
        price: tirePrice,
        errors: rawForm.article.errors
      };

      this.workingOrder.tires.push(tire);
      this.noTiresSelected = false;
      this.calculateTotals();
      gungAddRemoveSpinner(target);
      this.resetForm(this.form);
    }
  }

  protected removeFromWorkingOrder(index: number): void {
    this.workingOrder.tires.splice(index, 1);
    this.calculateTotals();
  }

  protected calculateTotals(): void {
    if (this.workingOrder.tires.length === 0) {
      this.calculatedTotals = undefined;
      return;
    }

    const totals: { qty: number; total: number; currency: string } = { qty: 0, total: 0, currency: '' };
    for (const tire of this.workingOrder.tires) {
      totals.qty += tire.qty;
      totals.total += tire.price.value * tire.qty;
      totals.currency = tire.price.currency;
    }
    this.calculatedTotals = totals;
  }

  protected onClick(): void {
    window.open('https://cdn2.gung.io/colmec/partner-portal/hjulpositioner.jpg', '_blank');
  }
}
